import { zodResolver } from '@hookform/resolvers/zod';
import { BRAND, pageTitle } from '@palaccount/config';
import { SignInRequestSchema } from '@palaccount/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { Languages, LineChart, Loader2, ShieldCheck, Sparkles } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router';
import type { z } from 'zod';

import { api } from '@/api';
import { BrandMark } from '@/components/brand-mark';
import { LanguageSwitch } from '@/components/language-switch';
import { PasswordInput } from '@/components/password-input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { resetDashboardFilters } from '@/lib/dashboard-filters';
import { ME_QUERY_KEY, isReturningVisitor, markSessionSeen, serverMessageKey } from '@/lib/session';

type FormInput = z.input<typeof SignInRequestSchema>;
type FormOutput = z.output<typeof SignInRequestSchema>;

/**
 * The gateway's depth is built from STATIC layers — brand-tinted radial-gradient
 * blobs behind a faint grid, a gradient-clipped headline, glass over glow — on
 * the app's own theme tokens (`--primary`, `--foreground`, `--card`), so it
 * adapts to light and dark and mirrors under RTL. The only motion is the
 * reviewed `animate-pulse` breathing on the blobs (GEN-188: all animation goes
 * through the token table; no hand-written keyframes, and `animate-pulse` is
 * 2s, far under §4.17's three-a-second bar — the same gentle breathing the
 * product's skeletons already use).
 */
const BLOBS = [
  {
    className: 'size-[44rem] -top-52 -start-36',
    background: 'radial-gradient(circle at center, var(--primary), transparent 62%)',
  },
  {
    className: 'size-[40rem] -bottom-52 -end-28',
    background:
      'radial-gradient(circle at center, color-mix(in oklab, var(--primary) 60%, #7c3aed), transparent 62%)',
  },
  {
    className: 'size-[32rem] top-1/4 end-[22%]',
    background:
      'radial-gradient(circle at center, color-mix(in oklab, var(--primary) 55%, #06b6d4), transparent 60%)',
  },
] as const;

/**
 * GEN-S1 — Sign in. Two panels: the blue brand panel (1.15 share) and the
 * white form (540px, never wider than 48%). No side menu, no top bar.
 *
 * The rules carried here:
 *  - BR-GEN-S1-01: the button is greyed out until both boxes have content.
 *  - BR-GEN-S1-02: any credential failure shows ONE message that never says
 *    which half was wrong — the message is the server's, mapped to the
 *    dictionary; unknown server sentences (lockout variants) pass through.
 *  - ERR-GEN-S1-02: a malformed email is caught client-side, mirroring the
 *    contract schema the server enforces.
 *  - BR-GEN-S1-05: the password travels only in a POST body — the form
 *    carries method="post" so even the native no-JS fallback cannot put it
 *    in the web address (see the comment on the <form> below).
 *  - BR-GEN-S1-08: ع flips the document RTL and survives sign-in (ui-store).
 *  - Edge cases: caps-lock hint (PasswordInput), Enter submits (native form),
 *    the eye icon genuinely reveals.
 *
 * The prototype's "← Back to design system" link is scaffolding the spec
 * forbids shipping; it is deliberately absent. "Forgot password?" is drawn
 * (always visible per §6) and now leads to the recovery flow the spec leaves
 * as "[NOT IN PROTOTYPE — decide at build time]".
 */
export function SignInPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [serverError, setServerError] = useState<string | null>(null);

  const sessionEnded = Boolean((location.state as { sessionEnded?: boolean } | null)?.sessionEnded);
  // "Welcome back" only to someone this browser has seen sign in before.
  const returning = isReturningVisitor();

  useEffect(() => {
    document.title = pageTitle(t('auth.signIn'));
  }, [t]);

  const form = useForm<FormInput, unknown, FormOutput>({
    resolver: zodResolver(SignInRequestSchema),
    defaultValues: { email: '', password: '', rememberMe: false },
    mode: 'onSubmit',
  });

  const [email, password, rememberMe] = form.watch(['email', 'password', 'rememberMe']);
  const submitting = form.formState.isSubmitting;
  // BR-GEN-S1-01 — greyed out until both boxes contain at least one character.
  const canSubmit = Boolean(email) && Boolean(password) && !submitting;

  const onSubmit = form.handleSubmit(async (values) => {
    setServerError(null);
    try {
      const response = await api.auth.signIn({ body: values });
      if (response.status === 200) {
        // BR-DSH-00-16's second clause: dashboard filter choices "reset to
        // the delivered defaults on the NEXT SIGN-IN" — this event, and only
        // the successful branch of it. sessionStorage outlives a sign-out
        // inside one tab, so without this line the clause fails exactly on a
        // shared browser, which is who it protects.
        resetDashboardFilters();
        markSessionSeen();
        await queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
        void navigate('/', { replace: true });
        return;
      }
      // 401 / 403 / 429 all carry { message } with contract-fixed wording.
      const message = response.body.message;
      const key = serverMessageKey(message);
      setServerError(key ? t(key) : message);
    } catch {
      // ERR-GEN-S1-05 — the network died while checking.
      setServerError(t('auth.errOffline', { brand: BRAND.name }));
    }
  });

  const features = [
    { key: 'auth.feat1', Icon: ShieldCheck },
    { key: 'auth.feat2', Icon: Languages },
    { key: 'auth.feat3', Icon: LineChart },
  ] as const;

  return (
    <div className="relative isolate flex min-h-screen flex-col overflow-hidden bg-background text-foreground">
      {/* Layered backdrop — brand-tinted blobs breathing on the reviewed
          animate-pulse (GEN-188), behind a faint grid and a vignette. Purely
          decorative, and it stills under reduced motion. */}
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        {BLOBS.map((blob, index) => (
          <div
            key={index}
            style={{ background: blob.background, animationDelay: `${index * 0.9}s` }}
            className={`absolute animate-pulse rounded-full opacity-55 blur-[64px] ${blob.className}`}
          />
        ))}
        <div
          className="absolute inset-0 opacity-60 [mask-image:radial-gradient(ellipse_80%_80%_at_50%_40%,#000,transparent_78%)]"
          style={{
            backgroundImage:
              'linear-gradient(to right, color-mix(in oklab, var(--foreground) 7%, transparent) 1px, transparent 1px), linear-gradient(to bottom, color-mix(in oklab, var(--foreground) 7%, transparent) 1px, transparent 1px)',
            backgroundSize: '64px 64px',
          }}
        />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_35%,var(--background)_92%)]" />
      </div>

      {/* Top bar: language switch is always reachable (BR-GEN-S1-08). */}
      <header className="relative z-20 flex items-center justify-between px-6 py-5 sm:px-10">
        <BrandMark className="lg:hidden" />
        <div className="ms-auto">
          <LanguageSwitch />
        </div>
      </header>

      <div className="relative z-10 mx-auto grid w-full max-w-6xl flex-1 items-center gap-10 px-6 pb-16 sm:px-10 lg:grid-cols-2 lg:gap-16">
        {/* ── The hero: the brand, reimagined as a gateway ────────────────── */}
        <section className="hidden flex-col gap-9 lg:flex">
          <BrandMark />
          <div className="grid gap-5">
            <span className="inline-flex w-fit items-center gap-2 rounded-full border border-border/60 bg-card/50 px-3.5 py-1.5 text-xs font-medium text-muted-foreground backdrop-blur">
              <Sparkles className="size-3.5 text-primary" aria-hidden />
              {t('auth.secLine')}
            </span>
            <h2 className="max-w-xl text-balance bg-gradient-to-br from-foreground via-primary to-foreground bg-clip-text text-4xl font-bold leading-[1.1] tracking-tight text-transparent xl:text-5xl">
              {t('auth.tagline')}
            </h2>
          </div>
          <ul className="grid max-w-md gap-3.5">
            {features.map(({ key, Icon }) => (
              <li
                key={key}
                className="flex items-center gap-3.5 rounded-2xl border border-border/50 bg-card/40 px-4 py-3 text-sm font-medium backdrop-blur-md transition-colors hover:border-primary/40 hover:bg-card/60"
              >
                <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-sm shadow-primary/40">
                  <Icon className="size-[1.125rem]" aria-hidden />
                </span>
                {t(key)}
              </li>
            ))}
          </ul>
        </section>

        {/* ── The sign-in card — frosted glass floating over the aurora ───── */}
        <section className="mx-auto w-full max-w-md">
          <div className="rounded-3xl border border-border/60 bg-card/70 p-7 shadow-2xl shadow-primary/20 ring-1 ring-foreground/5 backdrop-blur-2xl sm:p-9">
            <div className="mb-7 grid gap-1.5">
              <h1 className="text-2xl font-bold tracking-tight">{t('auth.signIn')}</h1>
              <p className="text-sm text-muted-foreground">
                {t(returning ? 'auth.signInSubReturning' : 'auth.signInSub')}
              </p>
            </div>

            {sessionEnded ? (
              <p
                role="status"
                className="mb-5 rounded-xl border border-border/60 bg-muted/60 px-3.5 py-2.5 text-sm"
              >
                {t('auth.sessionEnded')}
              </p>
            ) : null}

            {/* BR-GEN-S1-05 — "The password must never appear in the web address."
                The submit handler always talks JSON over POST, but a <form> with
                no method attribute falls back to a NATIVE GET SUBMIT when the JS
                handler is not there to preventDefault — a broken bundle away from
                `/?email=…&password=…` in the address bar, the history, and every
                proxy log. method="post" makes the fallback a POST of the current
                URL instead, so no failure mode puts the password in a URL. */}
            <form noValidate method="post" onSubmit={onSubmit} className="grid gap-5">
              <div className="grid gap-2">
                <Label htmlFor="email">{t('auth.emailLabel')}</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder={t('auth.emailPh')}
                  dir="ltr"
                  className="h-11 bg-background/60"
                  aria-invalid={Boolean(form.formState.errors.email)}
                  {...form.register('email')}
                />
                {form.formState.errors.email ? (
                  <p className="text-sm text-destructive">{t('auth.errEmailFormat')}</p>
                ) : null}
              </div>

              <div className="grid gap-2">
                <Label htmlFor="password">{t('auth.passwordLabel')}</Label>
                <PasswordInput
                  id="password"
                  autoComplete="current-password"
                  className="h-11 bg-background/60"
                  {...form.register('password')}
                />
              </div>

              <div className="flex items-center justify-between gap-4">
                <Label htmlFor="rememberMe" className="font-normal">
                  <Checkbox
                    id="rememberMe"
                    checked={Boolean(rememberMe)}
                    onCheckedChange={(checked) => form.setValue('rememberMe', checked === true)}
                  />
                  {t('auth.rememberMe')}
                </Label>
                {/* Always visible (§6), and it now goes somewhere. */}
                <Link to="/forgot-password" className="text-sm font-medium text-primary hover:underline">
                  {t('auth.forgot')}
                </Link>
              </div>

              {serverError ? (
                <p role="alert" className="text-sm text-destructive">
                  {serverError}
                </p>
              ) : null}

              <Button
                type="submit"
                disabled={!canSubmit}
                className="h-[52px] w-full text-base font-semibold shadow-lg shadow-primary/40 transition-transform hover:-translate-y-0.5"
              >
                {submitting ? (
                  <Loader2 className="animate-spin" aria-label={t('auth.checking')} />
                ) : null}
                {t('auth.signIn')}
              </Button>
            </form>

            <p className="mt-7 text-center text-sm text-muted-foreground">
              {t('auth.noAccount')}{' '}
              <Link to="/sign-up" className="font-semibold text-primary hover:underline">
                {t('auth.createOne')}
              </Link>
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}
